import * as React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import type { Option } from 'modules/core/components/Popover/Content'

import Actions from './Actions'
import Preview from './Preview'
import Default, { FileData } from './Preview/Default'
import Image from './Preview/Image'
import New from './Preview/New'
import Pdf from './Preview/PDF'
import Video from './Preview/Video'

export interface CurrentFileOptions {
  newFile: false
  fileName: string
  file: FileData
  options: Option[]
  inlinePath: string
}

export interface NewFileOptions {
  newFile: true
  fileName: string
  options: Option[]
}

export type FileOptions = CurrentFileOptions | NewFileOptions

interface Props {
  fileOptions: FileOptions
  showDeleteOnly?: boolean
}

const regExImage = new RegExp(/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i)
const regExPDF = new RegExp(/\.(pdf)$/i)
const regExVideo = new RegExp(/\.(mp4)$/i)

const File = ({ fileOptions, showDeleteOnly }: Props) => {
  const [showPreview, setShowPreview] = React.useState<boolean>(false)

  const content = () => {
    if (fileOptions.newFile) {
      return <New fileName={fileOptions.fileName} />
    }

    // Using RegExp instead of checking the file.attributes.content_type here,
    // because changing the extension of the file to gibberish, it was still getting previewed.
    // But when we were downloading it since the extension was weird, it could generate problems.
    if (regExImage.test(fileOptions.fileName)) {
      return <Image src={fileOptions.inlinePath} />
    } else if (regExPDF.test(fileOptions.fileName)) {
      return <Pdf src={fileOptions.inlinePath} />
    } else if (regExVideo.test(fileOptions.fileName)) {
      return <Video src={fileOptions.inlinePath} />
    } else {
      return <Default file={fileOptions.file} />
    }
  }

  return (
    <Box
      width="full"
      flexDirection="row"
      padding="s16"
      alignItems="center"
      justifyContent="spaceBetween"
      background="white"
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{
        topLeft: 'abs008',
        topRight: 'abs008',
        bottomLeft: 'abs008',
        bottomRight: 'abs008',
      }}
    >
      <Box gap="s8" flexDirection="row" overflowX="hidden">
        <Icon.Medium icon="file-filled" />
        <Text.Normal ellipsis color="grey800">
          {fileOptions.fileName}
        </Text.Normal>
      </Box>
      <Actions
        showDeleteOnly={showDeleteOnly}
        setShowPreview={setShowPreview}
        options={fileOptions.options}
        loading={fileOptions.newFile}
      />
      {showPreview && (
        <Preview
          fileName={fileOptions.fileName}
          onClose={() => setShowPreview(false)}
          content={content()}
        />
      )}
    </Box>
  )
}

export default observer(File)
